import React, { useState } from 'react'
import { useProgramContext } from '@/context/ProgramContext'
import { useFeatureLimit } from '@/hooks/useFeatureLimit'
import { useWebhooks, useDeleteWebhook, useTestWebhook } from '@/hooks/useWebhooks'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { PremiumFeature } from '@/components/ui/premium-feature'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { 
  Webhook, 
  Plus, 
  Edit, 
  Trash2, 
  TestTube, 
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  Globe
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'
import { useTranslation } from '@/hooks/useTranslation'

interface WebhookSettingsProps {
  onCreateWebhook: () => void
  onEditWebhook: (webhook: any) => void
}

const getEventTypeLabels = (t: (key: string) => string) => ({
  new_bug_report: t('webhookSettings.bugReports'),
  new_feature_request: t('webhookSettings.featureRequests'), 
  new_review: t('webhookSettings.reviews'),
  new_survey_response: t('webhookSettings.surveyResponses')
} as const)

const EVENT_TYPE_COLORS = {
  new_bug_report: 'bg-red-100 text-red-800 border-red-200',
  new_feature_request: 'bg-blue-100 text-blue-800 border-blue-200',
  new_review: 'bg-green-100 text-green-800 border-green-200',
  new_survey_response: 'bg-purple-100 text-purple-800 border-purple-200'
} as const

export function WebhookSettings({ onCreateWebhook, onEditWebhook }: WebhookSettingsProps) {
  const { t } = useTranslation()
  const { programId } = useProgramContext()
  const { data: webhooksData, isLoading, error } = useWebhooks(programId)
  const { allowed: canCreateWebhooks, limit, currentUsage, planId, message } = useFeatureLimit('max_webhooks', { programId })
  const deleteWebhookMutation = useDeleteWebhook()
  const testWebhookMutation = useTestWebhook()
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [webhookToDelete, setWebhookToDelete] = useState<any>(null)

  const webhooks = webhooksData?.webhooks || []
  const hasWebhooks = webhooks.length > 0
  const canAddMore = canCreateWebhooks && (limit === -1 || currentUsage < limit)
  const eventTypeLabels = getEventTypeLabels(t)


  const handleDeleteWebhook = (webhook: any) => {
    setWebhookToDelete(webhook)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!webhookToDelete || !programId) return
    
    await deleteWebhookMutation.mutateAsync({
      programId,
      webhookId: webhookToDelete.id
    })
    
    setDeleteDialogOpen(false)
    setWebhookToDelete(null)
  }

  const handleTestWebhook = async (webhook: any) => {
    if (!programId) return
    
    await testWebhookMutation.mutateAsync({
      programId,
      webhookId: webhook.id
    })
  }

  const getStatusInfo = (webhook: any) => {
    if (!webhook.is_active) {
      return {
        icon: AlertCircle,
        color: 'text-gray-600',
        message: t('webhookSettings.webhookIsDisabled'),
        label: t('webhookSettings.disabled')
      }
    }
    
    if (webhook.last_failure_message) {
      return {
        icon: AlertCircle,
        color: 'text-red-600',
        message: webhook.last_failure_message,
        label: t('webhookSettings.failed')
      }
    }
    
    if (webhook.last_triggered_at) {
      return {
        icon: CheckCircle,
        color: 'text-green-600',
        message: t('webhookSettings.lastTriggered', { time: formatDistanceToNow(new Date(webhook.last_triggered_at), { addSuffix: true }) }),
        label: t('webhookSettings.active')
      }
    }
    
    return {
      icon: Clock,
      color: 'text-gray-600',
      message: t('webhookSettings.neverTriggered'),
      label: t('webhookSettings.pending')
    }
  }

  if (!canCreateWebhooks) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="w-5 h-5" />
            {t('webhookSettings.webhookIntegrations')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PremiumFeature isLocked={true} message={t('webhookSettings.webhookIntegrationsAvailableOnPro')}>
            <div className="text-center py-8">
              <Webhook className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('webhookSettings.webhookIntegrations')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('webhookSettings.getRealTimeNotifications')}
              </p>
              <Button disabled variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                {t('webhookSettings.createWebhook')}
              </Button>
            </div>
          </PremiumFeature>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="w-5 h-5" />
            {t('webhookSettings.webhookIntegrations')}
            {limit > 0 && (
              <Badge variant="secondary" className="ml-auto">
                {currentUsage} / {limit}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-32" />
                    <div className="flex gap-2">
                      <Skeleton className="h-8 w-16" />
                      <Skeleton className="h-8 w-16" />
                      <Skeleton className="h-8 w-16" />
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-5 w-24" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <p className="text-red-600">{t('webhookSettings.failedToLoadWebhooks')}</p>
            </div>
          ) : !hasWebhooks ? (
            <div className="text-center py-8">
              <Globe className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('webhookSettings.noWebhooksConfigured')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('webhookSettings.createFirstWebhookDescription')}
              </p>
              <Button 
                onClick={onCreateWebhook}
                disabled={!canAddMore}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('webhookSettings.createYourFirstWebhook')}
              </Button>
            </div>
          ) : (
            <TooltipProvider>
              <div className="space-y-4">
                {webhooks.map((webhook) => {
                const statusInfo = getStatusInfo(webhook)
                const StatusIcon = statusInfo.icon
                
                return (
                  <div
                    key={webhook.id}
                    className={cn(
                      "border border-gray-200 rounded-lg p-5 bg-white hover:border-gray-300 transition-colors",
                      !webhook.is_active && "bg-gray-50/50"
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Header Row */}
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {webhook.name}
                          </h3>
                          <div className="flex items-center gap-2">
                            <StatusIcon className={cn("w-4 h-4", statusInfo.color)} />
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={cn("text-sm font-medium cursor-help", statusInfo.color)}>
                                  {statusInfo.label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>{statusInfo.message}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                        
                        {/* Event Types */}
                        <div className="flex flex-wrap gap-2">
                          {webhook.event_types.map((eventType: string) => (
                            <span
                              key={eventType}
                              className={cn(
                                "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium",
                                EVENT_TYPE_COLORS[eventType as keyof typeof EVENT_TYPE_COLORS]
                              )}
                            >
                              {eventTypeLabels[eventType as keyof typeof eventTypeLabels]}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTestWebhook(webhook)}
                              disabled={!webhook.is_active || testWebhookMutation.isPending}
                              className="h-8 w-8 p-0 hover:bg-gray-100 transition-colors"
                            >
                              <TestTube className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>{t('webhookSettings.testWebhook')}</p>
                          </TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEditWebhook(webhook)}
                              className="h-8 w-8 p-0 hover:bg-gray-100 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>{t('webhookSettings.editWebhook')}</p>
                          </TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteWebhook(webhook)}
                              disabled={deleteWebhookMutation.isPending}
                              className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>{t('webhookSettings.deleteWebhook')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )
              })}
              
              {canAddMore && (
                <Button 
                  onClick={onCreateWebhook}
                  variant="outline" 
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('webhookSettings.addAnotherWebhook')}
                </Button>
              )}
              
              {!canAddMore && limit > 0 && (
                <div className="text-center text-sm text-gray-600 py-2">
                  {t('webhookSettings.reachedMaxWebhooks', { limit })}
                </div>
              )}
              </div>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('webhookSettings.deleteWebhook')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('webhookSettings.deleteWebhookConfirmation', { webhookName: webhookToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('webhookSettings.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('webhookSettings.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}